import { ImageIcon, type LucideIcon } from 'lucide-react'
import { useAuthedImageUrl } from '@/hooks/use-authed-image-url'
import { cn } from '@/utils/cn'

/**
 * The icon under a photo: it covers the fetch, a missing photo and a failed one, so an image
 * slot never collapses or shows a broken-image glyph. Put it first in a `relative` box.
 */
export function ImageFallback({ Icon }: { Icon: LucideIcon }) {
  return (
    <span
      aria-hidden
      className="absolute inset-0 flex items-center justify-center text-muted-foreground"
    >
      <Icon className="size-1/2" />
    </span>
  )
}

export interface ThumbnailProps {
  /** Auth-gated `/api/v1/files/...` path, a blob: / data: URL, or nothing yet. */
  url: string | null | undefined
  /** Alt text, already translated; '' when the label next to it says the same. */
  alt: string
  /** Shown while loading and when there is no photo. Default: a picture icon. */
  Icon?: LucideIcon
  /** Size and radius, e.g. `size-16 rounded-xl`. */
  className?: string
}

/**
 * A list or close-up thumbnail. The bytes sit behind the auth-gated files endpoint, which a
 * bare `<img src>` cannot reach, so the URL goes through `useAuthedImageUrl`; the image loads
 * lazily and decodes off the main thread. A local preview (blob:) and an image a read carries
 * inline (data:, a stored capture's thumbnail) are drawn as they are, with no request. Without
 * a photo the slot keeps its icon and, when `alt` says something, its name.
 */
export function Thumbnail({ url, alt, Icon = ImageIcon, className }: ThumbnailProps) {
  const src = useAuthedImageUrl(url)
  return (
    <span className={cn('relative block shrink-0 overflow-hidden rounded-xl bg-muted', className)}>
      <ImageFallback Icon={Icon} />
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="relative size-full object-cover"
        />
      ) : (
        alt && <span className="sr-only">{alt}</span>
      )}
    </span>
  )
}
